import * as React from "react";
import { SimpleContainerModel, SimpleContainerProps } from "./typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps
} from "components/flexFramework/domain/BlossomComponentFactory";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";

const DEFAULT_WIDTH = "100";

export const SimpleContainer = (props: SimpleContainerProps) => {
  const blossomComponent = BlossomComponentFactory.get(props as BlossomComponentFactoryProps);
  const context = props.context;

  if (!blossomComponent) {
    return null;
  }

  const model = blossomComponent.getModel<SimpleContainerModel>();
  const { name, mobileWidth, title, width, innerTitle, tabletWidth } = model;
  const modules = blossomComponent.getBlossomComponents();

  if (mobileWidth === "hidden") {
    return null;
  }

  // Return null for regions with no title, inner title and all "empty" modules
  if (
    title &&
    innerTitle &&
    modules &&
    modules.length > 0 &&
    modules.filter(module => module.shouldRender()).length === 0
  ) {
    return null;
  }

  const headingClass =
    "uitk-type-heading-600 all-t-padding-three uitk-cell all-cell-1-1 all-l-padding-zero";
  const regionSpecificClasses = getRegionSpecificClasses(blossomComponent.name);
  const gridWidth = regionWidthToGridMap[width || DEFAULT_WIDTH];
  const gridTabletWidth = regionWidthToGridMap[tabletWidth || DEFAULT_WIDTH];
  const gridMobileWidth = regionWidthToGridMap[mobileWidth || DEFAULT_WIDTH];

  const flexContent = (
    <>
      {title && <h2 className={headingClass}>{title}</h2>}
      {innerTitle && <h2 className={headingClass}>{innerTitle}</h2>}
      <BlossomComponents blossomComponent={blossomComponent} context={context} />
    </>
  );

  const commonClassNames = [
    "uitk-grid uitk-cell",
    regionSpecificClasses,
    getExtraClassNameBasedOnParameters(model)
  ]
    .filter(classes => classes !== "")
    .join(" ");

  return (
    <Viewport>
      <ViewSmall>
        <div
          className={`SimpleContainer${
            name === "dx-page-header" ? " elevation-4" : ""
          } ${commonClassNames} ${gridMobileWidth}`}
        >
          {flexContent}
        </div>
      </ViewSmall>
      <ViewMedium>
        <div
          className={`SimpleContainer${
            name === "dx-page-header" ? " elevation-4" : ""
          } ${commonClassNames} ${gridTabletWidth}`}
        >
          {flexContent}
        </div>
      </ViewMedium>
      <ViewLarge>
        <div
          className={`${commonClassNames} ${gridWidth} ${
            name === "dx-page-header" ? "elevation-4" : "SimpleContainer"
          }`}
        >
          {flexContent}
        </div>
      </ViewLarge>
    </Viewport>
  );
};

/**
 * Return the classes corresponding to a specific region. This is
 * a temporary solution until Module groups are implemented
 * @param {string} regionName - Name of region
 * @return {string} classes to decorate region
 */
/* istanbul ignore next */
const getRegionSpecificClasses = (regionName: string): string => {
  switch (regionName) {
    case "wizard":
      return "wizardHero all-b-padding-six all-b-margin-six";
    case "FullWidthBreadcrumbs":
      return "fullWidthBreadcrumbs";
    case "CallToActionGroup":
      return "callToActionGroup";
    case "StorefrontWizard":
      return "storefrontWizard";
    case "all-cell-1-3":
      return "subRegion all-cell-1-3";
    case "dx-sidebar-content":
      return "dx-sidebar-content all-x-padding-zero";
    case "dx-sidebar-content-wizard":
      return "dx-sidebar-content dx-sidebar-content-wizard";
    case "SEO links":
      return "SEOLinks";
    case "RecentlyBookedReviewedNoBorderBkg":
      return "recentlyBookedReviewed noBorder noBackground";
  }

  return "";
};

const regionWidthToGridMap = {
  "25": "all-cell-1-4",
  "33": "all-cell-1-3",
  "50": "all-cell-1-2",
  "66": "all-cell-2-3",
  "75": "all-cell-3-4",
  "100": "all-cell-1-1"
};

const buildClassNameToParameterConditionMap = (model: SimpleContainerModel) => ({
  fullScreenWidth: model.fullWidth,
  dxWhiteBackground: model.backgroundColor === "white",
  "region-bottom-border": model.border === "bottom",
  "l-x-padding-three xl-x-padding-three all-y-padding-six": model.name === "dx-main-content",
  // TODO: clean up when experiment is finished
  "all-b-padding-six":
    model.name === "dx-sidebar-content" || model.name === "dx-sidebar-content-wizard",
  dxPageContent: model.name === "dx-page-content"
});

const getExtraClassNameBasedOnParameters = (model: SimpleContainerModel) => {
  const classMap = buildClassNameToParameterConditionMap(model);

  return Object.keys(classMap)
    .filter(x => classMap[x])
    .join(" ");
};

export default SimpleContainer;
